"use client";
import {
  EVERFLOW_EVENT_NAME_TO_ID_MAP,
  EVERFLOW_LOADED_EVENT_NAME,
  EVERFLOW_OFFER_ID,
  EverflowEventName,
} from "@portal/app/_common/types/everflow";
import EverflowSDK from "@everflow/everflow-sdk";
import { useCallback, useSyncExternalStore } from "react";
import { getEnvironmentName } from "@portal/app/_common/lib/edgeUtils";

function subscribeToEverflow(onStoreChange: () => void) {
  const handler = () => onStoreChange();
  window.addEventListener(EVERFLOW_LOADED_EVENT_NAME, handler);
  return () => {
    window.removeEventListener(EVERFLOW_LOADED_EVENT_NAME, handler);
  };
}

function getEverflowTransactionId() {
  return EverflowSDK.getTransactionId(EVERFLOW_OFFER_ID);
}

function getServerEverflowTransactionId() {
  return undefined;
}

export const useEverflow = () => {
  // useSyncExternalStore avoids hydration mismatch: getServerSnapshot returns
  // undefined during SSR, and getSnapshot reads the real value client-side.
  // It also avoids the react-hooks/set-state-in-effect lint violation that
  // useState + useEffect would require.
  const everflowTransactionId = useSyncExternalStore(
    subscribeToEverflow,
    getEverflowTransactionId,
    getServerEverflowTransactionId
  );

  /**
   * Sends an Everflow event. This should be used for internal events; for
   * core CPL conversions, use `conversion` wrapper instead.
   */
  const event = useCallback((maybeEvent?: EverflowEventName) => {
    return EverflowSDK.conversion({
      offer_id: EVERFLOW_OFFER_ID,
      ...(maybeEvent && { event_id: EVERFLOW_EVENT_NAME_TO_ID_MAP[maybeEvent] }),
      parameters: {
        environment: getEnvironmentName(),
      },
    });
  }, []);

  /**
   * Sends a core Everflow CPL conversion.
   *
   * A core CPL conversion is a special-case of an Everflow event with no
   * event ID.
   */
  const conversion = useCallback(() => event(), [event]);

  return {
    conversion,
    event,
    everflowTransactionId,
  };
};
